import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '@mode/capra';
import { ChartTypes } from '@mode/shared/contract-common';
import { RangeInputFieldComponent } from './range-input-field.component';

export const StatusStartAboveDomainMax =
  'The Min value is greater than the current maximum value in your data; some colors may not display on your chart.';
export const StatusMiddleAboveDomainMax =
  'The Midpoint value is greater than the current maximum value in your data; some colors may not display on your chart.';
export const StatusEndBelowDomainMin =
  'The Max value is less than the current minimum value in your data; some colors may not display on your chart.';
export const StatusMiddleBelowDomainMin =
  'The Midpoint value is less than the current minimum value in your data; some colors may not display on your chart.';
export const StatusGeneric =
  'One or more of your range values is not sequentially ordered with the Min, Midpoint, or Max. Some colors may not appear on your chart.';

@Component({
  standalone: true,
  selector: 'mode-range-inputs',
  imports: [CommonModule, FormsModule, IconComponent, RangeInputFieldComponent],
  templateUrl: './range-inputs.component.html',
  styleUrls: ['./range-inputs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeInputsComponent implements OnChanges {
  @Input() domainMin: number | undefined;
  @Input() domainMax: number | undefined;
  @Input() minValue: number | undefined;
  @Input() midpointValue: number | undefined;
  @Input() maxValue: number | undefined;
  @Input() gradientType: ChartTypes.GradientType | undefined;
  @Input() isDate = false;
  @Input() isBinned = false;

  @Output() rangeUpdated = new EventEmitter<{
    min: number | undefined;
    mid: number | undefined;
    max: number | undefined;
  }>();
  @Output() isValid = new EventEmitter<boolean>();

  // Local copies so we don't modify the original inputs
  min: number | undefined;
  mid: number | undefined;
  max: number | undefined;

  domainMidpoint: number | undefined;

  hasValidRange = true;
  hasValidDomain = true;
  statusMessage = '';

  readonly Diverging = ChartTypes.GradientType.DIVERGING;

  ngOnChanges(): void {
    this.min = this.minValue;
    this.mid = this.midpointValue;
    this.max = this.maxValue;

    this.calculateMidpoint();
    this.validateRange();
  }

  calculateMidpoint() {
    const actualMin = this.min ?? this.domainMin;
    const actualMax = this.max ?? this.domainMax;

    this.domainMidpoint = actualMin != null && actualMax != null ? (actualMin + actualMax) / 2 : undefined;
  }

  onKeyUp(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Enter' && input != null) {
      // Enter should submit
      input.blur();
    }
  }

  onInputCommit() {
    this.calculateMidpoint();
    this.validateRange();
    // Emit the current valid state
    this.isValid.emit(this.hasValidRange);
    if (this.hasValidRange) {
      // Emit the values
      this.rangeUpdated.emit({
        min: this.min,
        mid: this.gradientType === ChartTypes.GradientType.DIVERGING ? this.mid : undefined,
        max: this.max,
      });
    }
  }

  onValueChanged(value: number | undefined, position: 'min' | 'mid' | 'max') {
    switch (position) {
      case 'min':
        this.min = value;
        break;
      case 'mid':
        this.mid = value;
        break;
      case 'max':
        this.max = value;
        break;
    }
    this.onInputCommit();
  }

  validateRange() {
    // Validate the input values first. Any explicitly entered values must be ascending.
    this.hasValidRange = this.validateInputValues();
    if (!this.hasValidRange) {
      this.hasValidDomain = true;
      this.statusMessage = 'Ranges must be sequential numeric values.';
      return;
    }
    // Next validate the values against the domain. This will be a warning and not an error.
    this.validateDomain();
  }

  validateInputValues() {
    const values =
      this.gradientType === ChartTypes.GradientType.SEQUENTIAL ? [this.min, this.max] : [this.min, this.mid, this.max];

    const nonNullValues = values.filter((value): value is number => value != null);

    // Valid if all entered values are ascending.
    return !nonNullValues.some((value, index, arr) => value > arr[index + 1] ?? value);
  }

  validateDomain() {
    if (this.domainMin == null || this.domainMax == null) {
      this.hasValidDomain = true;
      this.statusMessage = '';
      return;
    }
    const min = this.min ?? this.domainMin;
    const max = this.max ?? this.domainMax;
    const mid = this.mid ?? (min + max) / 2;
    const values = this.gradientType === ChartTypes.GradientType.SEQUENTIAL ? [min, max] : [min, mid, max];

    // values should be ascending. It is invalid if a value is greater than the next value.
    const isInvalid = values.some((value, index, arr) => value > (arr[index + 1] ?? value));
    if (isInvalid) {
      // Generate the proper message
      this.hasValidDomain = false;
      if (this.max == null) {
        // No max set. Check if middle or start is greater than the domain max
        if (this.min != null && this.min > this.domainMax) {
          this.statusMessage = StatusStartAboveDomainMax;
          return;
        } else if (this.mid != null && this.mid > this.domainMax) {
          this.statusMessage = StatusMiddleAboveDomainMax;
          return;
        }
      }
      if (this.min == null) {
        if (this.max != null && this.max < this.domainMin) {
          this.statusMessage = StatusEndBelowDomainMin;
          return;
        } else if (this.mid != null && this.mid < this.domainMin) {
          this.statusMessage = StatusMiddleBelowDomainMin;
          return;
        }
      }
      this.statusMessage = StatusGeneric;
      return;
    }

    this.hasValidDomain = true;
    this.statusMessage = '';
  }
}
